import React from "react"
import { Role } from "@myvp/todos/src/gql/graphql"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import {
  ClockApiProvider,
  clockQueryOptions,
  defaultClockApi,
  useClockApi,
} from "@myvp/shared/src/clock/clock-api"
import { createVirtualClockUpdateTask } from "@myvp/shared/src/clock/virtual-clock"
import {
  getUserVirtualTimeQueryOptions,
  createUserVirtualClockApi,
  type VirtualTimeStudyRoleContext,
} from "@myvp/todos/src/uat-time-travel/user-virtual-time.model"

const UserClock = (props: {
  studyRoleContext: VirtualTimeStudyRoleContext | undefined
  virtualTimeDisabled?: boolean
  children?: React.ReactNode
}) => {
  const { data: isVirtualTimeActivated, isLoading } = useQuery({
    ...getUserVirtualTimeQueryOptions(props.studyRoleContext),
    enabled: !props.virtualTimeDisabled,
    select: (data): boolean =>
      Boolean(typeof data !== "symbol" && data.virtualTime),
  })

  const queryClient = useQueryClient()
  const clockApi = React.useMemo(() => {
    if (isVirtualTimeActivated && props.studyRoleContext) {
      return createUserVirtualClockApi(queryClient, props.studyRoleContext)
    } else {
      return defaultClockApi()
    }
  }, [isVirtualTimeActivated, props.studyRoleContext, queryClient])

  if (isLoading) {
    return null
  }
  return (
    <ClockApiProvider value={clockApi}>
      {isVirtualTimeActivated && props.studyRoleContext && (
        <VirtualClockUpdater {...props.studyRoleContext} />
      )}
      {props.children}
    </ClockApiProvider>
  )
}

export default UserClock

const VirtualClockUpdater = (props: {
  studyId: string
  role: Role
  participantRelatedUserId: string
}) => {
  const { data: shouldUpdate } = useQuery({
    ...getUserVirtualTimeQueryOptions({
      studyId: props.studyId,
      role: props.role,
      participantRelatedUserId: props.participantRelatedUserId,
    }),
    select: (data) =>
      typeof data !== "symbol" &&
      !!data?.virtualTime &&
      !data.virtualTime.paused,
    staleTime: Infinity,
  })
  const queryClient = useQueryClient()
  const clockApi = useClockApi()

  React.useEffect(() => {
    if (!shouldUpdate) return
    return createVirtualClockUpdateTask((update) => {
      let nextNow: Date | undefined
      queryClient.setQueryData(
        getUserVirtualTimeQueryOptions({
          studyId: props.studyId,
          role: props.role,
          participantRelatedUserId: props.participantRelatedUserId,
        }).queryKey,
        (prev) => {
          if (typeof prev === "symbol" || !prev?.virtualTime) return prev
          const nextVirtualTime = update(prev.virtualTime)
          nextNow = nextVirtualTime.virtualNow
          return {
            ...prev,
            virtualTime: {
              ...prev.virtualTime,
              ...nextVirtualTime,
            },
          }
        }
      )
      if (nextNow !== undefined) {
        if (nextNow.getUTCSeconds() === 0) {
          queryClient.refetchQueries({
            queryKey: clockQueryOptions(clockApi).queryKey,
          })
        } else {
          queryClient.setQueryData(
            clockQueryOptions(clockApi).queryKey,
            new Date(nextNow)
          )
        }
      }
    })
  }, [
    shouldUpdate,
    props.role,
    props.participantRelatedUserId,
    props.studyId,
    clockApi,
    queryClient,
  ])

  return null
}
